import { createInertiaApp } from "@inertiajs/react"
import { type ReactNode, createElement } from "react"
import { createRoot, hydrateRoot } from "react-dom/client"

// Temporary type definition, until @inertiajs/react provides one
interface ResolvedComponent {
  default: ReactNode & { layout?: (page: ReactNode) => ReactNode }
}

const appName = "SEOpiloto Blog"

// Check if browser supports View Transitions API
const supportsViewTransitions =
  typeof document !== "undefined" && "startViewTransition" in document

// Add View Transitions CSS
if (typeof document !== "undefined") {
  const style = document.createElement("style")
  style.textContent = `
    /* View Transitions API animations */
    @view-transition {
      navigation: auto;
    }

    /* Customize the default fade */
    ::view-transition-old(root) {
      animation: fade-out 200ms ease-out;
    }

    ::view-transition-new(root) {
      animation: fade-in 300ms ease-in;
    }

    @keyframes fade-out {
      from {
        opacity: 1;
      }
      to {
        opacity: 0;
      }
    }

    @keyframes fade-in {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }

    /* Smooth slide for main content */
    ::view-transition-old(main),
    ::view-transition-new(main) {
      animation-duration: 300ms;
    }

    ::view-transition-old(main) {
      animation-name: slide-out-to-left;
    }

    ::view-transition-new(main) {
      animation-name: slide-in-from-right;
    }

    @keyframes slide-out-to-left {
      from {
        transform: translateX(0);
        opacity: 1;
      }
      to {
        transform: translateX(-30px);
        opacity: 0;
      }
    }

    @keyframes slide-in-from-right {
      from {
        transform: translateX(30px);
        opacity: 0;
      }
      to {
        transform: translateX(0);
        opacity: 1;
      }
    }

    /* Fallback for browsers without View Transitions API */
    @supports not (view-transition-name: none) {
      #app {
        transition: opacity 150ms ease-in-out;
      }
      #app.page-transitioning {
        opacity: 0;
      }
    }
  `
  document.head.appendChild(style)
}

void createInertiaApp({
  title: (title) => (title ? `${title} - ${appName}` : appName),

  resolve: async (name) => {
    // Lazy load only public blog pages
    const pages = import.meta.glob<ResolvedComponent>(
      "../pages/public/**/*.tsx",
    )
    const importPage = pages[`../pages/${name}.tsx`]

    if (!importPage) {
      console.error(`Missing Inertia page component: '${name}.tsx'`)
      throw new Error(`Page not found: ${name}`)
    }

    const page = await importPage()

    // Blog pages don't use persistent layout
    // Each page controls its own layout
    return page
  },

  setup({ el, App, props }) {
    // Wrap rendering in View Transition if supported
    if (supportsViewTransitions && document.startViewTransition) {
      document.startViewTransition(() => {
        if (el.hasChildNodes()) {
          hydrateRoot(el, createElement(App, props))
        } else {
          createRoot(el).render(createElement(App, props))
        }
      })
    } else {
      // Fallback: simple opacity transition
      const appEl = document.getElementById("app")
      appEl?.classList.add("page-transitioning")

      // Render the page
      if (el.hasChildNodes()) {
        hydrateRoot(el, createElement(App, props))
      } else {
        createRoot(el).render(createElement(App, props))
      }

      // Remove transition class after render
      setTimeout(() => {
        appEl?.classList.remove("page-transitioning")
      }, 150)
    }
  },

  progress: {
    color: "#6366f1",
    showSpinner: false,
  },
})
